<template>
  <div class="form-contact" :class="[{ preview: preview }, ...wrapperClasses]">
    <div class="title flex mb-2">
      {{ title }}<span v-if="mandatory" class="required ml-2">*</span>
      <div
        v-if="!preview"
        @click="onClickHelp"
        class="flex cursor-pointer ml-2"
      >
        <feather-icon
          :icon="'HelpCircleIcon'"
          class="inline-flex rounded-full"
          svgClasses="h-5"
        ></feather-icon>
      </div>
    </div>
    <div v-if="showPlainText" class="flex">
      <div class="editable-input-area" @click="isEdit = true">
        <div class="p-4">
          <div class="flex items-start justify-between">
            <div class="title">名前</div>
          </div>
          <div>
            <span>{{ displayInputName }}</span>
          </div>
        </div>
        <div class="p-4">
          <div class="flex items-start justify-between">
            <div class="title">電話番号</div>
          </div>
          <div>
            <span>{{ displayInputTel }}</span>
          </div>
        </div>
        <div class="p-4">
          <div class="flex items-start justify-between">
            <div class="title">メールアドレス</div>
          </div>
          <div>
            <span>{{ displayInputEmail }}</span>
          </div>
        </div>
      </div>
      <vs-tooltip
        style="height: fit-content"
        color="#2D9CDB"
        :text="`登録日時: ${createdDateLabel}`"
      >
        <common-updated-label
          :updatedAt="updateDateLabel"
          :createdAt="createDateLabel"
        />
      </vs-tooltip>
      <div class="mandatory-switch flex" v-if="showMandatorySwitch">
        <vs-switch color="#2D9CDB" class="ml-6" v-model="mandatoryValue" />
        <span class="ml-2">必須</span>
      </div>
      <template v-if="showStatus">
        <status-chip
          v-if="isStatusFixed"
          class="ml-3 mr-2"
          :label="status.label"
          :color="status.color"
        ></status-chip>
        <status-dropdown-select
          v-else
          class="ml-3 mr-2"
          :options="filteredStatusOptions"
          :value.sync="status"
          :onClickOption="onStatusChange"
          clickEvent
        ></status-dropdown-select>
      </template>
    </div>
    <div v-else class="flex row-gap-8 col-gap-8 w-full">
      <div class="name-form-item flex-1 py-3">
        <div class="form-item-title pb-1">
          <span>名前</span>
        </div>
        <div class="flex justify-between">
          <vs-input
            class="mr-1"
            v-model="editInputName"
            color="#2D9CDB"
            placeholder="名前"
          />
          <vs-input
            class="ml-1"
            v-model="editInputNameKana"
            color="#2D9CDB"
            placeholder="カナ"
          />
        </div>
        <div class="form-item-title pb-1 mt-4">
          <span>電話番号</span>
        </div>
        <div class="flex justify-between">
          <vs-input
            v-model="editInputTel"
            color="#2D9CDB"
            :danger="!isEditInputTelValid"
            danger-text="電話番号の形式で入力してください"
          />
        </div>
        <div class="form-item-title pb-1 mt-4">
          <span>メールアドレス</span>
        </div>
        <div class="flex justify-between">
          <vs-input
            v-model="editInputEmail"
            color="#2D9CDB"
            :danger="!isEditInputEmailValid"
            danger-text="メールアドレスの形式で入力してください"
          />
        </div>
      </div>
      <div class="btn-area flex gap-3 p-3" v-if="!preview">
        <common-button
          class="save-btn-template"
          title="保存"
          type="filled"
          :click="
            () => {
              onClickSaveButton();
            }
          "
          :btnStyle="{
            paddingLeft: 0,
            paddingRight: 0,
            width: '70px',
            height: '40px',
          }"
          :disabled="preview || !isFormValidated"
        ></common-button>
        <common-button
          class="cancel-btn-template"
          type="border"
          color="#BDBDBD"
          :click="
            () => {
              cancelEdit();
            }
          "
          :btnStyle="{
            paddingLeft: 0,
            paddingRight: 0,
            width: '70px',
            height: '40px',
            fontSize: '12px',
          }"
          text-color="#757575"
          title="キャンセル"
          :disabled="preview"
        ></common-button>
      </div>
    </div>
  </div>
</template>

<script>
import { ColorMixin } from "@/mixins";
import CommonButton from "../molecules/CommonButton.vue";
import { toYMDHm_jp } from "@/utils/entity/DateUtil";
import ValidationUtil from "@/utils/entity/ValidationUtil";
import CommonUpdatedLabel from "../molecules/CommonUpdatedLabel.vue";
import StatusDropdownSelect from "../organisms/StatusDropdownSelect.vue";
import StatusChip from "../molecules/StatusChip.vue";

export default {
  components: {
    CommonButton,
    CommonUpdatedLabel,
    StatusDropdownSelect,
    StatusChip,
  },
  mixins: [ColorMixin],
  name: "O.FormContact",
  props: {
    wrapperClasses: {
      type: Array, // Array<String>
      default() {
        return [];
      },
    },
    preview: {
      type: Boolean,
      default: false,
    },
    onClickSave: {
      type: Function,
      default: () => {},
    },
    onSwitchChange: {
      type: Function,
      default: () => {},
    },
    onClickHelp: {
      type: Function,
      default: () => {},
    },
    onStatusChange: {
      type: Function,
      default: () => {},
    },
    title: {
      type: String,
    },
    inputValue: {
      type: Object,
    },
    mandatory: {
      type: Boolean,
    },
    isAdmin: {
      type: Boolean,
    },
    createdDate: {
      type: Number,
    },
    createDate: {
      type: Number,
    },
    updateDate: {
      type: Number,
    },
    initialSwitchValue: {
      type: Boolean,
      default: false,
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
    initialStatusValue: {
      type: Object,
    },
  },
  data() {
    return {
      isEdit: false,
      mandatoryValue: this.initialSwitchValue,
      editInputValue: null,

      editInputName: "",
      editInputNameKana: "",
      editInputTel: "",
      editInputEmail: "",
    };
  },
  watch: {
    mandatoryValue(value) {
      this.onSwitchChange(value);
    },
  },
  created() {
    this.mandatoryValue = this.initialSwitchValue;
    this.initializeEditInput();
  },
  computed: {
    isEditInputTelValid() {
      return (
        this.editInputTel == "" ||
        ValidationUtil.validateTel_jp(this.editInputTel)
      );
    },
    isEditInputEmailValid() {
      return (
        this.editInputEmail == "" ||
        ValidationUtil.isValidEmail(this.editInputEmail)
      );
    },

    displayInputName() {
      if (
        this.inputValue != null &&
        (this.inputValue.name || this.inputValue.name_kana)
      ) {
        return `${this.inputValue.name} ${this.inputValue.name_kana}`;
      } else {
        return "-";
      }
    },
    displayInputTel() {
      if (this.inputValue != null && this.inputValue.tel) {
        return `${this.inputValue.tel}`;
      } else {
        return "-";
      }
    },
    displayInputEmail() {
      if (this.inputValue != null && this.inputValue.email) {
        return `${this.inputValue.email}`;
      } else {
        return "-";
      }
    },
    createDateLabel() {
      return toYMDHm_jp(this.createDate);
    },
    updateDateLabel() {
      return toYMDHm_jp(this.updateDate);
    },
    createdDateLabel() {
      return toYMDHm_jp(this.createdDate);
    },
    showPlainText() {
      if (!this.preview && !this.isEdit) {
        return true;
      } else {
        return false;
      }
    },
    showMandatorySwitch() {
      if (this.isAdmin && this.showPlainText) {
        return true;
      } else {
        return false;
      }
    },
    isFormValidated() {
      return (
        /*
        this.editInputName.trim().length > 0 &&
        this.editInputNameKana.trim().length > 0 &&
        this.editInputTel.trim().length > 0 &&
        this.editInputEmail.trim().length > 0 &&
        */
        this.isEditInputEmailValid && this.isEditInputTelValid
      );
    },
    filteredStatusOptions() {
      return this.statusOptions.filter(element => element.value != 1);
    },
    isStatusFixed() {
      return this.status.value == 1 || !this.isAdmin;
    },
    showStatus() {
      return !this.preview && this.mandatory;
    },
  },

  methods: {
    initializeEditInput() {
      this.editInputName = this.inputValue ? this.inputValue.name : "";
      this.editInputNameKana = this.inputValue ? this.inputValue.name_kana : "";
      this.editInputTel = this.inputValue ? this.inputValue.tel : "";
      this.editInputEmail = this.inputValue ? this.inputValue.email : "";
      this.status = this.initialStatusValue;
    },
    cancelEdit() {
      this.isEdit = false;
      this.initializeEditInput();
    },
    onClickSaveButton() {
      this.isEdit = false;
      this.onClickSave({
        name: this.editInputName,
        name_kana: this.editInputNameKana,
        tel: this.editInputTel,
        email: this.editInputEmail,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";
@import "@/assets/scss/aisea/_typography.scss";
@import "@/assets/scss/aisea/hull-insurance.scss";
.form-contact {
  min-height: 264px;
  width: 100%;
  &.preview {
    pointer-events: none;
  }
  .required {
    font-weight: bold;
    color: $aisea-red;
  }
  .update-date-label {
    font-size: 12px;
  }
  .editable-input-area {
    margin-right: 18px;
    transition: background-color 0.3s;
    flex: 1;
    cursor: pointer;
    .editable-input-text {
      line-height: 2.2;
    }
    &:hover {
      background: $hover-background-blue;
    }
  }
  .vs-con-input-label {
    width: 100%;
  }
  .btn-area {
    margin-top: 33px;
  }
}
</style>
